import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, Siren, AtSign } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Avatar } from "@/components/Avatar";
import { useMentions } from "@/components/dashboard/AttentionPanel";

export function MentionBadge() {
  const { data } = useMentions();
  const count = data?.count ?? 0;
  if (!count) return null;
  return (
    <Link to="/team" className="inline-flex items-center gap-1.5 rounded-full border-2 border-black bg-[#FACC15] px-3 py-1.5 text-sm font-bold" data-testid="mentions-badge" title="Teammates mentioned you">
      <AtSign className="h-4 w-4" /> {count} need{count === 1 ? "s" : ""} your attention
    </Link>
  );
}

export function AlarmBadge({ stats }) {
  const count = stats?.alarm_count ?? 0;
  const l2 = stats?.l2_count ?? 0;
  return (
    <Link
      to="/team/admin"
      data-testid="alarm-badge-counter"
      className={`inline-flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-sm font-bold ${count > 0 ? "border-[#DC2626] bg-[#DC2626] text-white alarm-pulse" : "border-black bg-white text-black"}`}
      title="Priority alarms (L3 / Out-of-SLA)"
    >
      <Siren className="h-4 w-4" />
      <span>{count} alarm{count === 1 ? "" : "s"}</span>
      {l2 > 0 && <span className="ml-1 rounded-full bg-black/20 px-2 text-xs" data-testid="alarm-badge-l2">+{l2} at L2</span>}
    </Link>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/tickets/stats").then((r) => r.data), refetchInterval: 60000 });

  const tab = ({ isActive }) =>
    `rounded-full px-4 py-1.5 text-sm font-bold border-2 transition-colors ${isActive ? "bg-black text-white border-black" : "bg-white text-black border-transparent hover:border-black"}`;

  return (
    <header className="sticky top-0 z-30 border-b-2 border-black bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Brand subtitle={user?.is_master ? "Master admin" : isAdmin ? "Admin" : "Executive"} />
        <nav className="flex items-center gap-2" data-testid="dashboard-nav">
          <NavLink to="/team" end className={tab} data-testid="nav-dashboard-tab">Tickets</NavLink>
          {isAdmin && <NavLink to="/team/admin" className={tab} data-testid="nav-admin-tab">Admin panel</NavLink>}
        </nav>
        <div className="flex items-center gap-3">
          <MentionBadge />
          {isAdmin && <AlarmBadge stats={stats} />}
          <Link to="/team/profile" className="flex items-center gap-2 rounded-full border-2 border-transparent py-0.5 pl-0.5 pr-3 hover:border-black" data-testid="profile-link" title="My profile">
            <Avatar user={user} size={34} testId="header-avatar" />
            <span className="hidden text-sm font-semibold sm:block" data-testid="current-user-name">{user?.name}</span>
          </Link>
          <button onClick={logout} className="btn-ghost !px-3 !py-2" data-testid="logout-button" title="Log out">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </div>
    </header>
  );
}
